import api from './api'

export interface Reel {
  id: number
  user: {
    id: number
    username: string
    firstName: string
    lastName: string
    profileImage?: string
  }
  videoUrl: string
  thumbnailUrl?: string
  caption?: string
  musicTitle?: string
  musicArtist?: string
  duration?: number
  viewCount: number
  likeCount: number
  commentCount: number
  shareCount: number
  reshareCount: number
  visibility: string
  hashtags?: string[]
  createdAt: string
  updatedAt: string
  likedByMe: boolean
  isReshared?: boolean
  // Only present when this reel was reshared (reposted) by the profile owner —
  // the timestamp the reshare happened. Drives the "Reposted" badge + sort.
  resharedAt?: string
}

export interface ReelComment {
  id: number
  user: {
    id: number
    username: string
    firstName: string
    lastName: string
    profileImage?: string
  }
  reelId: number
  parentId?: number
  content: string
  likeCount: number
  isEdited: boolean
  createdAt: string
  updatedAt: string
}

interface ReelFeedResponse {
  content: Reel[]
  totalElements: number
  totalPages: number
  last: boolean
}

interface CommentResponse {
  content: ReelComment[]
  totalElements: number
  totalPages: number
}

const reelService = {
  // Get feed reels (requires auth)
  getFeed: async (page: number = 0, size: number = 10): Promise<ReelFeedResponse> => {
    const response = await api.get(`/reels/feed?page=${page}&size=${size}`)
    return response.data.data
  },

  // Get public reels
  getPublic: async (page: number = 0, size: number = 10): Promise<ReelFeedResponse> => {
    const response = await api.get(`/reels/public?page=${page}&size=${size}`)
    return response.data.data
  },

  // Get trending reels
  getTrending: async (page: number = 0, size: number = 10): Promise<ReelFeedResponse> => {
    const response = await api.get(`/reels/trending?page=${page}&size=${size}`)
    return response.data.data
  },

  // Get single reel
  getReel: async (id: number): Promise<Reel> => {
    const response = await api.get(`/reels/${id}`)
    return response.data.data
  },

  // Get user's reels
  getUserReels: async (userId: number, page: number = 0, size: number = 10): Promise<ReelFeedResponse> => {
    const response = await api.get(`/reels/user/${userId}?page=${page}&size=${size}`)
    return response.data.data
  },

  // Get reels this user has reshared (reposted from others). Each Reel carries
  // resharedAt + the ORIGINAL author in reel.user (never duplicated). Drives
  // the "Reposted" items on the profile Reels tab.
  getResharedReels: async (userId: number, page: number = 0, size: number = 10): Promise<ReelFeedResponse> => {
    const response = await api.get(`/reels/user/${userId}/reshared?page=${page}&size=${size}`)
    return response.data.data
  },

  // Search reels
  search: async (keyword: string, page: number = 0, size: number = 10): Promise<ReelFeedResponse> => {
    const response = await api.get(`/reels/search?keyword=${encodeURIComponent(keyword)}&page=${page}&size=${size}`)
    return response.data.data
  },

  // Create reel
  create: async (data: {
    videoUrl: string
    thumbnailUrl?: string
    caption?: string
    musicTitle?: string
    musicArtist?: string
    visibility?: string
    hashtags?: string[]
  }): Promise<Reel> => {
    const response = await api.post('/reels', data)
    return response.data.data
  },

  // Delete reel
  delete: async (id: number): Promise<void> => {
    await api.delete(`/reels/${id}`)
  },

  // Like reel
  like: async (id: number): Promise<Reel> => {
    const response = await api.post(`/reels/${id}/like`)
    return response.data.data
  },

  // Unlike reel
  unlike: async (id: number): Promise<Reel> => {
    const response = await api.delete(`/reels/${id}/like`)
    return response.data.data
  },

  // Increment view count
  view: async (id: number): Promise<void> => {
    await api.post(`/reels/${id}/view`)
  },

  // Share reel
  share: async (id: number): Promise<void> => {
    await api.post(`/reels/${id}/share`)
  },

  // Reshare reel (idempotent)
  reshare: async (id: number): Promise<Reel> => {
    const response = await api.post(`/reels/${id}/reshare`)
    return response.data.data
  },

  // Remove reshare from reel
  unreshare: async (id: number): Promise<Reel> => {
    const response = await api.delete(`/reels/${id}/reshare`)
    return response.data.data
  },

  // Get reel count for user
  getCount: async (userId: number): Promise<number> => {
    const response = await api.get(`/reels/count/${userId}`)
    return response.data.data
  },

  // Comment methods
  getComments: async (reelId: number, page: number = 0, size: number = 20): Promise<CommentResponse> => {
    const response = await api.get(`/reels/${reelId}/comments?page=${page}&size=${size}`)
    return response.data.data
  },

  addComment: async (reelId: number, content: string, parentId?: number): Promise<ReelComment> => {
    const response = await api.post(`/reels/${reelId}/comments`, { content, parentId })
    return response.data.data
  },

  // Delete a comment (reelId kept for API consistency but not used in endpoint)
  deleteComment: async (_reelId: number, commentId: number): Promise<void> => {
    await api.delete(`/reels/comments/${commentId}`)
  },

  // Bookmark methods
  toggleBookmark: async (reelId: number): Promise<boolean> => {
    const response = await api.post(`/reels/${reelId}/bookmark`)
    return response.data.data
  },

  isBookmarked: async (reelId: number): Promise<boolean> => {
    const response = await api.get(`/reels/${reelId}/bookmarked`)
    return response.data.data
  },

  // Get saved reels
  getSavedReels: async (page: number = 0, size: number = 10): Promise<ReelFeedResponse> => {
    const response = await api.get(`/reels/saved?page=${page}&size=${size}`)
    return response.data.data
  }
}

export default reelService